import React, { useCallback, useMemo, useRef } from "react";
import * as Scrivito from "scrivito";
import { Tree } from "@hogartrabe/react-arborist-vrh";
import { useTranslate } from "hooks";
import { useOpenAndScrollLeftSidebarTopologyItem } from "hooks/wizardHooks";
import cx from "classnames";
import { defaultOptions } from "variables/nodeDefaultOptions";
import useResizeObserver from "use-resize-observer";
import { normalizeCanSiblingsForFireProtection } from "utils/wizardHelpers/wizardHelpers";
import {
  useSelectedTopologyItemId,
  useStructuredTopology,
  useSubType,
} from "stores/wizardStore";
import { WizardTopologyMenuNode } from "./WizardTopologyMenuNode";

import s from "./WizardLeftSidebarExtension.module.scss";

const childrenAccessor = (d) => [
  ...(d.rightPlacedChildren || []),
  ...(d.sameLayer || []),
  ...(d.connectedByTopBracketChildren || []),
  ...(d.bottomPlacedChildren || []),
  ...(d.directlyConnected || []),
  ...(d.chainedChildren || []),
];

export const WizardTopologyMenu = Scrivito.connect(() => {
  const translate = useTranslate();
  const selectedTopologyItemId = useSelectedTopologyItemId();
  const treeRef = useRef(null);

  const translations = {
    folder: {
      nodePaste: translate("PROJECT_NODE_PASTE"),
      configure: translate("CONFIGURE"),
    },
  };

  const structuredTopology = useStructuredTopology();
  const subType = useSubType();
  useOpenAndScrollLeftSidebarTopologyItem({
    selectedTopologyItemId,
    treeRef,
  });

  const treeData = useMemo(
    () =>
      subType === "fireprotection"
        ? normalizeCanSiblingsForFireProtection(structuredTopology)
        : structuredTopology,
    [structuredTopology, subType],
  );

  const handleNodeToggle = useCallback((id) => {
    treeRef.current.open(id);
    treeRef.current?.scrollTo(id);
  }, []);

  if (!structuredTopology || !structuredTopology.id) {
    return null;
  }
  const topologyOptions = useMemo(
    () => ({
      ...defaultOptions,
      resetOnPaste: false,
      indentSize: 12,
    }),
    [],
  );

  const initalState = useMemo(
    () =>
      structuredTopology?.bottomPlacedChildren
        ? {
            [structuredTopology.id]: true,
            [structuredTopology?.bottomPlacedChildren[0]?.id]: true,
          }
        : { [structuredTopology.id]: true },
    [structuredTopology],
  );

  const { ref, height } = useResizeObserver();

  return (
    <div className={cx("pt-2", s.topologyMenuWrapper)} ref={ref}>
      <Tree
        data={[treeData]}
        ref={treeRef}
        initialOpenState={initalState}
        childrenAccessor={childrenAccessor}
        rowHeight={52}
        width={350}
        indent={topologyOptions.indentSize}
        height={height}
        openByDefault={false}
        className={cx(s.treeScrollBar)}
      >
        {(props) => (
          <WizardTopologyMenuNode
            {...props}
            options={topologyOptions}
            translations={translations}
            handleNodeToggle={handleNodeToggle}
          />
        )}
      </Tree>
    </div>
  );
});
